import type { ToolContent } from "./tool-content";

const genericDeContent = (name: string, action: string): ToolContent => ({
  seoTitle: `${name} Kostenlos Online – Einfaches & Faires Tool | Picamini.online`,
  seoDescription: `Kostenloses Online-Tool ${name.toLowerCase()}. ${action} 100% kostenlos, ohne Registrierung. Perfekt für Lehrer, Schüler, Meetings & Events.`,
  h1Title: `${name} Kostenlos Online – Schnell, Fair & Direkt im Browser`,
  intro: `${action} Speziell entwickelt für Lehrkräfte, Schüler, Event-Moderatoren und Teams. Alles läuft zu 100% lokal und datenschutzkonform in deinem Browser ohne Konto-Zwang.`,
  whatIs: [
    `Das Tool ${name} ist eine vielseitige, kostenlose Webanwendung, die entwickelt wurde, um Entscheidungen, Namensauswahlen und Gruppenunterteilungen im Unterricht und im Alltag schnell, fair und visuell ansprechend zu gestalten.`,
    `Egal ob du eine Unterrichtsstunde planst, ein Online-Meeting moderierst oder einen Spieleabend veranstaltest: Diese Anwendung ersetzt manuelle Zettelziehungen durch blitzschnelle visuelle Zufallsziehungen mit klarem Design und Soundeffekten.`,
    `Im Gegensatz zu unübersichtlichen Portalen erfordert unser ${name.toLowerCase()} keine Registrierung, keine Softwareinstallation und keine Angabe persönlicher Daten. Einfach die Webseite öffnen und sofort starten.`
  ],
  howTo: [
    `Öffne das Tool ${name} auf deinem Computer, Tablet oder Smartphone im Browser.`,
    "Trage deine Schülerliste, Namensauswahl oder Zahlen im Einstellungsfeld ein.",
    "Passe deine Einstellungen wie Sound-Effekte, Auswahlanzahl oder Wiederholungs-Optionen an.",
    "Klicke auf den Haupt-Button, um die faire Zufallsziehung zu starten.",
    "Verfolge die flüssige Animation und den Sound, während das Ergebnis gezogen wird.",
    "Nutze die Kopier- oder Druckfunktion, um das Ergebnis schnell zu teilen oder für deinen Unterricht aufzubewahren."
  ],
  features: [
    "100% Kostenlos mit unbegrenzter Nutzung ohne tägliche Limits",
    "Keine Registrierung und keine Angabe von E-Mail-Adressen erforderlich",
    "Browser-lokale Verarbeitung — Namenslisten verlassen niemals dein Gerät",
    "Optimiert für die Projektion an Beamer, Smartboards und interaktive Tafeln",
    "Interaktive Soundeffekte mit schnellem Stummschalt-Schalter",
    "Barrierefreie Tastatur-Steuerung (Leertaste zum Starten, Escape für Vollbild)",
    "Vollständig responsive für Smartphone, iPad, Android und Laptop",
    "Kopier- und Druckoptionen für deinen Unterrichtsablauf"
  ],
  benefits: [
    "Spart wertvolle Unterrichtszeit im Vergleich zu Zettellosen",
    "Garantiert mathematische Chancengleichheit und verhindert Vorwürfe von Bevorzugung",
    "Erhöht die Aufmerksamkeit und Motivation der Klasse durch visuelle Animationen",
    "Funktioniert nach dem Laden der Seite auch bei schwacher Internetverbindung zuverlässig",
    "Ablenkungsfreie Benutzeroberfläche ohne störende Werbe-Popups"
  ],
  proTips: [
    "Projiziere das Tool direkt an die Tafel, damit alle Schüler die Ziehung live mitverfolgen können.",
    "Aktiviere die Option 'Gezogene Namen entfernen', damit jeder Schüler einmal an die Reihe kommt.",
    "Speichere deine angepassten Namenslisten als Lesezeichen im Browser — die Liste bleibt automatisch im lokalen Speicher erhalten.",
    "Nutze die Druckfunktion für Vertretungsstunden und Klassenbuch-Nachweise."
  ],
  useCases: [
    {
      role: "Lehrkräfte & Pädagogen (Grundschule, Sekundarstufe)",
      description: "Sorge für faire Schüleraufrufe, ausgewogene Gruppenarbeiten, Sitzpläne und transparente Countdown-Timer im Klassenzimmer."
    },
    {
      role: "Moderatoren, Trainer & Event-Veranstalter",
      description: "Bringe Schwung in Meetings, Workshops und Spieleabende mit Verlosungsrädern, Entscheidungs-Tools und Punkteanzeigen."
    }
  ],
  detailedGuide: [
    {
      heading: `Ausführlicher Leitfaden zum Einsatz von ${name}`,
      paragraphs: [
        `In modernen Lern- und Arbeitsumgebungen sind Transparenz und Chancengleichheit entscheidend für die Motivation aller Beteiligten. Das Tool ${name.toLowerCase()} bietet eine verlässliche digitale Lösung, die menschliche Voreingenommenheit bei Alltagsentscheidungen ausschließt.`,
        `Unsere Anwendung nutzt bewährte Zufallsalgorithmen direkt im Browser des Benutzers, sodass jede eingetragene Option mathematisch genau dieselbe Chance hat, gezogen zu werden.`
      ],
      bulletPoints: [
        "Keine Ladeverzögerung: Sofortige Ergebnisse auf dem Bildschirm",
        "Voller Datenschutz: Alle Namen bleiben lokal im Browser-Speicher",
        "Plattformunabhängig ohne App-Download nutzbar"
      ]
    }
  ],
  comparisonTable: {
    title: "Vergleich: Picamini.online gegenüber manuellen Methoden",
    headers: ["Kriterium", "Picamini.online", "Klassischer Papier-Zettel"],
    rows: [
      {
        feature: "Vorbereitungszeit",
        pickamini: "Sofort (0 Sekunden)",
        competitor: "Minutenlanges Zettelschneiden",
        paper: "Zeitintensiv"
      },
      {
        feature: "Chancengleichheit",
        pickamini: "100% Algorithmetisch & Transparent",
        competitor: "Anfällig für Faltung & Haptik",
        paper: "Unsicher"
      },
      {
        feature: "Sichtbarkeit für die Klasse",
        pickamini: "Hervorragend am Beamer / Smartboard",
        competitor: "Nur aus der Nähe lesbar",
        paper: "Niedrig"
      }
    ]
  },
  ideasAndTemplates: [
    {
      title: "Unterrichtseinstieg & Abfrage",
      description: "Nutze das Tool zu Stundenbeginn, um den Schüler für die Wiederholung der letzten Stunde oder die Hausaufgabenpräsentation zu bestimmen.",
      exampleItems: ["Stundenwiederholung", "Hausaufgaben vortragen", "Leserunde"]
    },
    {
      title: "Gruppen- & Partnerarbeit",
      description: "Setze den Zufallsgenerator ein, um durchmischte Arbeitsgruppen zu bilden und soziale Dynamiken positiv zu fördern.",
      exampleItems: ["Partnerarbeit", "Gruppendiskussion", "Stationenlernen"]
    }
  ],
  probabilityMath: {
    title: "Mathematische Grundlagen der Zufallsauswahl",
    formula: "P(A) = 1 / N",
    explanation: "Jedes eingetragene Element besitzt exakt dieselbe gleichmäßige Wahrscheinlichkeit (P), gezogen zu werden, wobei N für die Gesamtzahl der gültigen Optionen steht.",
    keyConcepts: [
      {
        concept: "Gleichverteilung",
        detail: "Alle Optionen haben bei jedem Durchgang exakt die gleiche mathematische Gewinnchance."
      },
      {
        concept: "Unabhängigkeit",
        detail: "Sofern das Entfernen gezogener Optionen nicht aktiviert ist, bleibt jede Ziehung unabhängig von vorherigen Ergebnissen."
      }
    ]
  },
  faq: [
    {
      q: `Ist die Nutzung von ${name} kostenlos?`,
      a: "Ja, das Tool ist zu 100% kostenlos und unbegrenzt nutzbar. Es gibt keine Abos, keine versteckten Kosten und keine Registrierung."
    },
    {
      q: "Werden meine Schülerdaten auf Servern gespeichert?",
      a: "Nein. Alle eingegebenen Namen und Listen verbleiben ausschließlich im lokalen Speicher deines eigenen Browsers."
    },
    {
      q: "Kann ich die Anzeige auf dem Beamer oder der Tafel nutzen?",
      a: "Ja! Das Design ist mit großen Schriften und hohem Kontrast perfekt für interaktive Whiteboards, Beamer und Fernseher optimiert."
    }
  ]
});

// Specific German search intent overrides
const DE_TOOL_OVERRIDES: Record<string, Partial<ToolContent>> = {
  "zufalls-namensgenerator": {
    seoTitle: "Zufalls-Namensgenerator Kostenlos – Namen Ziehen | Picamini.online",
    seoDescription: "Zufalls-Namensgenerator kostenlos online. Namen ziehen für Schule, Verlosungen & Events. 100% kostenlos ohne Registrierung im Browser.",
    h1Title: "Zufalls-Namensgenerator – Schnell, Fair & Transparent",
    intro: "Ziehe Namen, Schüler oder Gewinner per Zufall mit flüssiger Animation und Sound. Ideal für Lehrer, Gruppenarbeiten und Gewinnspiele.",
  },
  "schueler-zufallsgenerator": {
    seoTitle: "Schüler-Zufallsgenerator für Lehrer & Unterricht | Picamini.online",
    seoDescription: "Schüler-Zufallsgenerator kostenlos für Lehrer. Klassenliste eingeben und fairen Schüler per Zufall für Aufrufe oder Aufgaben auswählen.",
    h1Title: "Schüler-Zufallsgenerator für den Unterricht",
    intro: "Sorge für Chancengleichheit und Aufmerksamkeit im Klassenzimmer. Trage deine Klassenliste ein und wähle Schüler ohne Bevorzugung aus.",
  },
  "gluecksrad-online": {
    seoTitle: "Glücksrad Online Drehen Kostenlos – Entscheidungsrad | Picamini.online",
    seoDescription: "Glücksrad online drehen kostenlos. Eigenes Glücksrad erstellen für Namen, Entscheidungen & Spiele. Bunt, schnell & ohne Anmeldung.",
    h1Title: "Glücksrad Online Drehen – Schnell Entscheidungen Treffen",
    intro: "Interaktives Online-Glücksrad zum Drehen von Namen, Aufgaben, Entscheidungen oder Preisen. Gestalte dein eigenes Glücksrad in Sekunden.",
  },
  "namensrad": {
    seoTitle: "Namensrad Online Kostenlos – Glücksrad für Namen | Picamini.online",
    seoDescription: "Namensrad online drehen kostenlos. Namen eingeben, Rad drehen und den Gewinner ziehen. Perfekt für Schule, Verlosungen & Partys.",
    h1Title: "Namensrad Online – Namen Per Glücksrad Ziehen",
    intro: "Trage deine Namen ein, drehe das farbenfrohe Glücksrad und ermittle den Gewinner mit Konfetti-Effekt und Musik.",
  },
  "ja-nein-rad": {
    seoTitle: "Ja-Nein-Rad Kostenlos – Ja oder Nein Glücksrad | Picamini.online",
    seoDescription: "Ja-Nein-Rad online drehen. Schnelle 50/50 Entscheidungen im Handumdrehen treffen. Kostenlos, direkt & einfach zu bedienen.",
    h1Title: "Ja-Nein-Rad – Schnelle Entscheidungen Per Zufall",
    intro: "Unentschlossen? Drehe das Ja-Nein-Glücksrad für eine sofortige neutrale Antwort auf deine Fragen.",
  },
  "gewinnspiel-rad": {
    seoTitle: "Gewinnspiel & Belohnungs-Rad Online Kostenlos | Picamini.online",
    seoDescription: "Gewinnspiel-Glücksrad kostenlos online. Erstelle dein eigenes Gewinnrad für Belohnungen, Tombolas & Klassenanreize.",
    h1Title: "Gewinnspiel & Belohnungs-Glücksrad",
    intro: "Belohne Schüler oder Teilnehmer mit einem attraktiven Gewinnrad. Trage Preise, Gutscheine oder Gutschriften ein und drehe los.",
  },
  "team-generator": {
    seoTitle: "Team-Generator Kostenlos – Zufallsteams Erstellen | Picamini.online",
    seoDescription: "Team-Generator kostenlos online. Teile Gruppen und Klassen in faire, ausgewogene Zufallsteams ein – mit nur 1 Klick.",
    h1Title: "Team-Generator – Zufallsteams Schnell & Fair Erstellen",
    intro: "Bilde Sport- oder Arbeitsgruppen ohne Streitigkeiten. Namensliste einfügen, gewünschte Teamanzahl wählen und sofort ausgewogene Teams erhalten.",
  },
  "gruppen-generator": {
    seoTitle: "Gruppen-Generator Kostenlos – Zufällige Gruppen | Picamini.online",
    seoDescription: "Gruppen-Generator kostenlos für Schule & Kurse. Teile Teilnehmer nach Gruppengröße oder Gruppenanzahl auf.",
    h1Title: "Gruppen-Generator für Lehrer & Teams",
    intro: "Erstelle Zufallsgruppen in Sekundenschnelle. Wähle flexibel, ob du die Anzahl der Gruppen oder die maximale Personenanzahl pro Tisch vorgeben möchtest.",
  },
  "wuerfel-online": {
    seoTitle: "Virtueller Würfel Online Kostenlos – Würfeln | Picamini.online",
    seoDescription: "Virtueller Würfel online kostenlos (W6, W10, W20, W100). Würfele interaktiv mit realistischer 3D-Animation im Browser.",
    h1Title: "Virtueller Würfel Online – Würfeln Ohne Echten Würfel",
    intro: "Würfel vergessen? Würfele online für Brettspiele, Rollenspiele oder den Matheunterricht mit animierter Physik und Sound.",
  },
  "muenze-werfen": {
    seoTitle: "Münze Werfen Online – Kopf oder Zahl Kostenlos | Picamini.online",
    seoDescription: "Münze werfen online kostenlos. Entscheidungs-Münzwurf mit 3D Kopf oder Zahl Animation im Browser.",
    h1Title: "Münze Werfen Online – Kopf oder Zahl",
    intro: "Wirf eine virtuelle Münze in die Luft und entscheide wer anfängt oder wer recht hat mit flüssiger Münz-Animation.",
  },
  "schul-timer": {
    seoTitle: "Schul- & Klassenzimmer-Timer Kostenlos | Picamini.online",
    seoDescription: "Klassenzimmer-Timer kostenlos für Lehrer. Großer Countdown am Beamer für Arbeitsphasen, Tests & Pausen.",
    h1Title: "Klassenzimmer-Timer & Schul-Countdown",
    intro: "Strukturierte Unterrichtszeiten mit einem großen, gut lesbaren Countdown-Timer. Ideal für Gruppenarbeiten und Prüfungen.",
  },
  "sitzplan-generator": {
    seoTitle: "Sitzplan-Generator Schule Kostenlos | Picamini.online",
    seoDescription: "Sitzplan-Generator kostenlos für Lehrer. Erstelle zufällige Sitzordnungen für Schulklassen in Reihen oder Tischgruppen.",
    h1Title: "Sitzplan-Generator für das Klassenzimmer",
    intro: "Verteile deine Schüler schnell und fair auf Sitzplätze im Raum. Vermeide Ablenkungen und drucke die Sitzordnung direkt aus.",
  }
};

export function getDeToolContent(slug: string, fallbackName: string): ToolContent {
  const base = genericDeContent(fallbackName, `Interaktives Tool für ${fallbackName.toLowerCase()}.`);
  const override = DE_TOOL_OVERRIDES[slug] || {};
  return {
    ...base,
    ...override,
  };
}
